import { z } from 'zod';
/**
 * Read-only views over the A2 tables (market.session_coverage, market.trading_calendar)
 * for the Quality and Calendar screens. Two GETs, no mutations; A5 may move them.
 *
 * Fractions come back as decimal STRINGS exactly as Postgres holds them (numeric(7,6)).
 * They are display statistics, not prices, but the API layer still never turns a
 * numeric column into a JS number — the client formats for display only.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'date must be a real date');
export const REAL_FEED_NAMES = ['iex', 'sip', 'otc'];
export const ADJUSTMENT_NAMES = ['raw', 'split', 'dividend', 'all'];
const dayCount = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
export const coverageQuerySchema = z
    .object({
    symbol: z
        .string()
        .regex(/^[A-Z][A-Z0-9.]{0,9}$/, 'symbol must be 1–10 upper-case letters/digits/dots')
        .optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    feed: z.enum(REAL_FEED_NAMES).default('iex'),
    adjustment: z.enum(ADJUSTMENT_NAMES).default('split'),
})
    .refine((q) => !q.symbol || (q.from && q.to), {
    message: 'from and to are required with symbol',
    path: ['from'],
})
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'from must not be after to',
    path: ['to'],
})
    .refine((q) => !q.from || !q.to || dayCount(q.from, q.to) <= 400, {
    message: 'range must not exceed 400 days',
    path: ['to'],
});
export const coverageSeriesSchema = z.object({
    symbol: z.string(),
    feed: z.enum(REAL_FEED_NAMES),
    adjustment: z.enum(ADJUSTMENT_NAMES),
    firstDate: isoDate,
    lastDate: isoDate,
    sessions: z.number().int().nonnegative(),
});
export const coverageSessionSchema = z.object({
    date: isoDate,
    regularExpected: z.number().int().nonnegative(),
    regularPopulated: z.number().int().nonnegative(),
    /** numeric(7,6) as a decimal string, e.g. "0.920513". */
    regularFraction: z.string(),
    extendedExpected: z.number().int().nonnegative(),
    extendedPopulated: z.number().int().nonnegative(),
    extendedFraction: z.string(),
    computedAt: z.string(),
});
export const coverageResponseSchema = z.object({
    /** Every (symbol, feed, adjustment) that has coverage rows — the selector's options. */
    series: z.array(coverageSeriesSchema),
    /** Present when `symbol` was requested. */
    selection: z
        .object({
        symbol: z.string(),
        feed: z.enum(REAL_FEED_NAMES),
        adjustment: z.enum(ADJUSTMENT_NAMES),
        from: isoDate,
        to: isoDate,
        sessions: z.array(coverageSessionSchema),
        summary: z.object({
            /** Calendar sessions with a coverage row, including any the feed has nothing for. */
            sessions: z.number().int().nonnegative(),
            /**
             * Sessions with zero bars. Counted and named, never averaged in: a session the feed has
             * nothing for is not a session at 0% coverage, and folding it into a mean discards real
             * sessions over an artifact of how a missing one is counted.
             */
            noDataSessions: z.number().int().nonnegative(),
            regularExpected: z.number().int().nonnegative(),
            regularPopulated: z.number().int().nonnegative(),
            /** Mean of the per-session regular fractions, decimal string; null with no sessions. */
            meanRegularFraction: z.string().nullable(),
            minRegularFraction: z.string().nullable(),
            maxRegularFraction: z.string().nullable(),
        }),
    })
        .nullable(),
});
export const calendarQuerySchema = z
    .object({ from: isoDate, to: isoDate })
    .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['to'] })
    .refine((q) => dayCount(q.from, q.to) <= 800, {
    message: 'range must not exceed 800 days',
    path: ['to'],
});
export const calendarSessionSchema = z.object({
    date: isoDate,
    /** UTC instants. */
    open: z.string(),
    close: z.string(),
    earlyClose: z.boolean(),
    source: z.string(),
    regularMinutes: z.number().int().nonnegative(),
});
export const calendarResponseSchema = z.object({
    from: isoDate,
    to: isoDate,
    sessions: z.array(calendarSessionSchema),
    /** Distinct `source` values present in the range, e.g. ["alpaca"] or ["nyse-rules"]. */
    sources: z.array(z.string()),
});
/**
 * What is actually in `market.indicator_5m`, by version.
 *
 * This exists because the version mechanism had no observability. That v1 rows survive a v2
 * recompute was inferrable from the write counts and from the migration containing no DELETE,
 * but it was not observable, and an inference is not a check. Once B2's `label_features` rows
 * point at a version, "which versions does this table actually hold, and does the registry
 * still agree with the hash recorded for the current one" stops being a curiosity.
 *
 * Read-only and cheap: counts and date ranges, never indicator values.
 */
export const indicatorVersionsQuerySchema = z.object({
    symbol: z
        .string()
        .regex(/^[A-Z][A-Z0-9.]{0,9}$/, 'symbol must be 1–10 upper-case letters/digits/dots')
        .optional(),
    feed: z.enum(REAL_FEED_NAMES).optional(),
    adjustment: z.enum(ADJUSTMENT_NAMES).optional(),
});
export const indicatorVersionScopeSchema = z.object({
    symbol: z.string(),
    feed: z.enum(REAL_FEED_NAMES),
    adjustment: z.enum(ADJUSTMENT_NAMES),
    version: z.number().int().positive(),
    rows: z.number().int().nonnegative(),
    firstSession: isoDate.nullable(),
    lastSession: isoDate.nullable(),
});
export const indicatorVersionRegistrySchema = z.object({
    version: z.number().int().positive(),
    definitionHash: z.string(),
    description: z.string().nullable(),
    createdAt: z.string(),
    /** True for the version this build of the code computes. */
    current: z.boolean(),
    /**
     * For the current version only: whether the registry still hashes to what the migration
     * recorded. False means the code and the table disagree about what a column MEANS, which is
     * the condition `indicators.recompute` refuses to write under.
     */
    hashMatchesRegistry: z.boolean().nullable(),
});
export const indicatorVersionsResponseSchema = z.object({
    /** Every version seeded by a migration, newest first. */
    registry: z.array(indicatorVersionRegistrySchema),
    /** Row counts per (symbol, feed, adjustment, version) actually present in the table. */
    scopes: z.array(indicatorVersionScopeSchema),
    /** The version this build computes, whatever the table holds. */
    currentVersion: z.number().int().positive(),
    /** sha256 of the live indicator registry. */
    registryHash: z.string(),
});
//# sourceMappingURL=marketdata.js.map